import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { useTickets } from 'contexts/TicketsContext';
import type { ExportTicketsRequestDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { Column } from 'declarations/types/common';
import type { NoopT } from 'declarations/types/common';
import {
  useCreateDmsScheduleIntegrationRequest,
  useFetchDmsScheduleAutomation,
} from 'hooks/api/useDmsScheduleAccounts';
import { useExportScheduleTickets, useExportTickets } from 'hooks/api/useTickets';
import { getStorageKeyForSection } from 'pages/TicketsPage/components/ColumnPickerSettingsPopup/components/ColumnPickerSettings/mockedColumns';
import { getScheduleExportColumnsToExport } from 'pages/TicketsPage/helpers/scheduleCsvExportMapping';

type UseHeaderT = {
  onChangeInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  resetSearchTerm: NoopT;
  openNewTicketPopup: NoopT;
  reassignTicketPopup: NoopT;
  shareTicketPopup: NoopT;
  handleExportClick: NoopT;
  handleExportScheduleClick: NoopT;
  openImportSchedulePopup: NoopT;
  openCloseTicketPopup: NoopT;
  isExportingTickets: boolean;
  isExportingScheduleTickets: boolean;
  searchText: string;
  handleOpenViewScheduleFilesPopup: NoopT;
  handleCloseViewScheduleFilesPopup: NoopT;
  isScheduleAutomationEnabled: boolean;
  isScheduleAutomationStatusLoading: boolean;
  lastGetDmsScheduleIntegrationRequest: string | null;
  hasRequestedGetDmsScheduleToday: boolean;
  handleCreateDmsScheduleIntegrationRequest: NoopT;
};

export const mapSearchParamsToExportRequest = (
  searchParams: URLSearchParams,
  activeTab: ActiveTabTypeE,
  scheduleCategoryId?: number
): ExportTicketsRequestDTO => {
  const sortParameter = searchParams.get(TicketsRefinementKeysE.Parameter) || undefined;
  const sortOrder = searchParams.get(TicketsRefinementKeysE.Order) || undefined;

  return {
    roofTopIds: searchParams.get(TicketsRefinementKeysE.RoofTopId) || undefined,
    agentRoofTopRole:
      (searchParams.get(TicketsRefinementKeysE.AgentRoofTopRole) as string) || undefined,
    sorting:
      sortParameter || sortOrder ? { parameter: sortParameter, order: sortOrder } : undefined,
    filter: {
      status: (searchParams.get(TicketsRefinementKeysE.Status) as string) || undefined,
      ticketAge: (searchParams.get(TicketsRefinementKeysE.TicketAge) as string) || undefined,
      roId: (searchParams.get(TicketsRefinementKeysE.RONumber) as string) || undefined,
      roNumber: searchParams.get(TicketsRefinementKeysE.RONumber) || undefined,
      assigneeId: (searchParams.get(TicketsRefinementKeysE.AssigneeId) as string) || undefined,
      roofTopId: searchParams.get(TicketsRefinementKeysE.RoofTopId) || undefined,
      assignedToMe: !!searchParams.get(TicketsRefinementKeysE.AssignedToMe) || undefined,
      pastMonth: !!searchParams.get(TicketsRefinementKeysE.PastMonth) || undefined,
      isArchived: ((): boolean | undefined => {
        const isArchivedParam = searchParams.get(TicketsRefinementKeysE.IsArchived);
        if (isArchivedParam === 'true') {
          return true;
        }
        if (isArchivedParam === 'false') {
          return false;
        }
        return undefined;
      })(),
      past30Days: !!searchParams.get(TicketsRefinementKeysE.Past30Days) || undefined,
      isSchedule: !!searchParams.get(TicketsRefinementKeysE.IsSchedule) || undefined,
      closedROAgeRange: (searchParams.get(TicketsRefinementKeysE.Schedule) as string) || undefined,
      assigneeName: searchParams.get(TicketsRefinementKeysE.Assignee) || undefined,
      ticketCategoryIds: ((): string | undefined => {
        if (activeTab !== ActiveTabTypeE.Schedule) {
          return searchParams.get(TicketsRefinementKeysE.Category) || undefined;
        }
        if (scheduleCategoryId) {
          return String(scheduleCategoryId);
        }
        return searchParams.get(TicketsRefinementKeysE.Category) || undefined;
      })(),
      ticketSubCategoryIds: ((): string | undefined => {
        const val = searchParams.get(TicketsRefinementKeysE.SubCategory) || '';
        return val === 'All' ? undefined : val || undefined;
      })(),
      requestSubCategoryIds:
        searchParams.get(TicketsRefinementKeysE.RequestSubCategoryIds) || undefined,
      dmsScheduleAccountIds:
        searchParams.get(TicketsRefinementKeysE.DMSScheduleAccounts) || undefined,
      searchTerm: searchParams.get(TicketsRefinementKeysE.SearchTerm) || undefined,
      workTypes: searchParams.get(TicketsRefinementKeysE.WorkTypes) || undefined,
    },
  };
};

const searchTermDelay = 300;
dayjs.extend(utc);

const useHeader = (): UseHeaderT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const {
    setNewTicketPopupVisibility,
    setReassignTicketPopupVisibility,
    setShareTicketPopupVisibility,
    setImportSchedulePopupVisibility,
    setCloseTicketPopupVisibility,
    setViewScheduleFilesPopupVisibility,
    updateSearchParam,
    clearSearchParam,
    getSearchParam,
    selectedRoofTop,
    selectedTicketsIds,
    activeTab,
    scheduleCategoryId,
  } = useTickets();
  const searchTermParam = getSearchParam(TicketsRefinementKeysE.SearchTerm);
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchTermParam || '');
  const [hasRequestedGetDmsScheduleToday, setHasRequestedGetDmsScheduleToday] = useState(false);
  const [exportTrigger, setExportTrigger] = useState(0);

  /**
   * Api hooks
   */
  const scheduleAutomationResult = useFetchDmsScheduleAutomation(selectedRoofTop);
  const dmsScheduleIntegrationMutation = useCreateDmsScheduleIntegrationRequest({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchDmsScheduleAutomationStatus]);
    },
  });

  /**
   * Handlers
   */
  const debouncedUpdateSearchParam = useDebouncedCallback(
    (value: string): void => updateSearchParam(TicketsRefinementKeysE.SearchTerm, value),
    searchTermDelay
  );
  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    const trimmedValue = value.trim();

    setSearchText(value);

    if (trimmedValue.length >= 3) {
      debouncedUpdateSearchParam(value);
    }

    if (!trimmedValue) {
      clearSearchParam(TicketsRefinementKeysE.SearchTerm);
    }
  };
  const openNewTicketPopup = (): void => setNewTicketPopupVisibility(true);
  const reassignTicketPopup = (): void => setReassignTicketPopupVisibility(true);
  const openCloseTicketPopup = (): void => setCloseTicketPopupVisibility(true);
  const shareTicketPopup = (): void => setShareTicketPopupVisibility(true);
  const openImportSchedulePopup = (): void => setImportSchedulePopupVisibility(true);
  const resetSearchTerm = (): void => {
    setSearchText('');
    clearSearchParam(TicketsRefinementKeysE.SearchTerm);
  };

  const getScheduleCsvColumnsForExport = (): string[] => {
    const storageKey = getStorageKeyForSection(ActiveTabTypeE.Schedule);
    const storedVisibleColumnsJSON = localStorage.getItem(storageKey);
    if (!storedVisibleColumnsJSON) {
      return [];
    }

    const storedVisibleColumns: Column[] = JSON.parse(storedVisibleColumnsJSON);
    const visibleColumnLabels = storedVisibleColumns
      .filter((column: Column) => column.visible)
      .map((column: Column) => column.label);

    return getScheduleExportColumnsToExport(visibleColumnLabels);
  };

  const exportParamsWithIds: ExportTicketsRequestDTO = useMemo(() => {
    const baseParams = {
      ...mapSearchParamsToExportRequest(searchParams, activeTab, scheduleCategoryId),
      filter: {
        ...mapSearchParamsToExportRequest(searchParams, activeTab, scheduleCategoryId).filter,
        ticketIds: selectedTicketsIds,
      },
    };

    if (activeTab === ActiveTabTypeE.Schedule) {
      const csvColumns = getScheduleCsvColumnsForExport();
      baseParams.filter = {
        ...baseParams.filter,
        columnsToExport: csvColumns,
      };
    }

    return baseParams;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exportTrigger is intentionally included to force recalculation when export is clicked
  }, [searchParams, selectedTicketsIds, activeTab, scheduleCategoryId, exportTrigger]);

  const exportTickets = useExportTickets(exportParamsWithIds, (result: Blob): void => {
    const fileName = `chirp_tickets_${new Date().toLocaleString()}.csv`;
    const blob = new Blob([result], { type: 'csv/text' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = href;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  });

  const exportScheduleTickets = useExportScheduleTickets(exportParamsWithIds, (result: Blob) => {
    const fileName = `chirp_tickets_schedule_${new Date().toLocaleString()}.csv`;
    const blob = new Blob([result], { type: 'csv/text' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = href;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  });

  const [pendingExportType, setPendingExportType] = useState<'tickets' | 'schedule' | null>(null);

  const handleExportClick = (): void => {
    // Trigger recalculation of exportParamsWithIds to read fresh column preferences
    setExportTrigger(prev => prev + 1);
    setPendingExportType('tickets');
  };

  const handleExportScheduleClick = (): void => {
    // Trigger recalculation of exportParamsWithIds to read fresh column preferences
    setExportTrigger(prev => prev + 1);
    setPendingExportType('schedule');
  };

  // Trigger refetch after exportParamsWithIds is recalculated with fresh column preferences
  useEffect(() => {
    if (pendingExportType === 'tickets') {
      setPendingExportType(null);
      exportTickets.refetch();
    } else if (pendingExportType === 'schedule') {
      setPendingExportType(null);
      exportScheduleTickets.refetch();
    }
  }, [exportTrigger, pendingExportType, exportTickets, exportScheduleTickets]);

  const handleOpenViewScheduleFilesPopup = useCallback((): void => {
    setViewScheduleFilesPopupVisibility(true);
  }, [setViewScheduleFilesPopupVisibility]);
  const handleCloseViewScheduleFilesPopup = useCallback((): void => {
    setViewScheduleFilesPopupVisibility(false);
  }, [setViewScheduleFilesPopupVisibility]);

  const handleCreateDmsScheduleIntegrationRequest = useCallback((): void => {
    dmsScheduleIntegrationMutation.mutate(getSearchParam(TicketsRefinementKeysE.RoofTopId) ?? '');
  }, [dmsScheduleIntegrationMutation, getSearchParam]);
  /**
   * Effects
   */
  useEffect(() => {
    // Clear searchText state value when user clears all filters
    if (!searchTermParam) {
      setSearchText('');
    }
  }, [searchTermParam]);

  useEffect(() => {
    const integrationRequestDate = scheduleAutomationResult?.data?.lastIntegrationRequest ?? null;
    if (!integrationRequestDate) {
      setHasRequestedGetDmsScheduleToday(false);
    }
    const lastDate = dayjs(integrationRequestDate).local().startOf('day');
    const today = dayjs().startOf('day');
    setHasRequestedGetDmsScheduleToday(lastDate.isSame(today));
  }, [scheduleAutomationResult]);

  return {
    searchText,
    onChangeInput,
    openNewTicketPopup,
    openImportSchedulePopup,
    reassignTicketPopup,
    openCloseTicketPopup,
    shareTicketPopup,
    handleExportClick,
    handleExportScheduleClick,
    isExportingTickets: exportTickets.isFetching,
    isExportingScheduleTickets: exportScheduleTickets.isFetching,
    resetSearchTerm,
    handleCloseViewScheduleFilesPopup,
    handleOpenViewScheduleFilesPopup,
    isScheduleAutomationEnabled: scheduleAutomationResult?.data?.isAutomationEnabled ?? false,
    isScheduleAutomationStatusLoading: scheduleAutomationResult.isFetching,
    lastGetDmsScheduleIntegrationRequest:
      scheduleAutomationResult?.data?.lastIntegrationRequest ?? null,
    hasRequestedGetDmsScheduleToday,
    handleCreateDmsScheduleIntegrationRequest,
  };
};

export default useHeader;
